import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CountryPhoneInput from './CountryPhoneInput';
import { useLanguage } from '../contexts/LanguageContext';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const ProfileModal = ({ user, onClose }) => {
  const { t } = useLanguage();
  // Initialize all hooks unconditionally
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    gender: 'MALE',
    phone: '',
    phoneCountryId: null,
    address: '',
    pcId: '',
    customerType: 'ONLINE',
    checkupDay: 'MONDAY',
    program: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [programOptions, setProgramOptions] = useState([]);
  const [emailValidation, setEmailValidation] = useState({
    isChecking: false,
    isValid: true,
    message: ''
  });
  const [usernameValidation, setUsernameValidation] = useState({
    isChecking: false,
    isValid: true,
    message: ''
  });

  // Fetch complete user profile data from backend
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) {
        setError(t('User data not available. Please try again.'));
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        const response = await axios.get(`/api/customers/${user.id}`);

        const userData = response.data;
        console.log('Fetched user profile data:', userData); // Debug log
        
        // Always set profile data, even if some fields are missing
        setProfileData({
          firstName: userData.firstName || user.firstName || '',
          lastName: userData.lastName || user.lastName || '',
          email: userData.email || user.email || '',
          username: userData.username || user.username || '',
          gender: userData.gender || 'MALE',
          phone: userData.phone || '',
          phoneCountryId: userData.phoneCountry?.id || null,
          address: userData.address || '',
          pcId: userData.pcId || '',
          customerType: userData.customerType || 'ONLINE',
          checkupDay: userData.checkupDay || 'MONDAY',
          program: userData.program || '',
          password: ''
        });
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        // Fallback to user data from context if API fails
        setProfileData({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          username: user.username || '',
          gender: 'MALE',
          phone: '',
          phoneCountryId: null,
          address: '',
          pcId: '',
          customerType: 'ONLINE',
          checkupDay: 'MONDAY',
          program: '',
          password: ''
        });
        setError(t('Failed to load complete profile data. Some fields may be empty.'));
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user?.id, user?.firstName, user?.lastName, user?.email]);

  const validateEmail = async (email, excludeUserId = null) => {
    if (!email || !email.includes('@')) {
      setEmailValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
      return;
    }

    setEmailValidation({
      isChecking: true,
      isValid: true,
      message: ''
    });

    try {
      const params = { email };
      if (excludeUserId) {
        params.excludeUserId = excludeUserId;
      }
      
      const response = await axios.get('/api/users/check-email', { params });
      
      if (response.data.exists) {
        setEmailValidation({
          isChecking: false,
          isValid: false,
          message: t('This email address is already registered')
        });
      } else {
        setEmailValidation({
          isChecking: false,
          isValid: true,
          message: ''
        });
      }
    } catch (error) {
      console.error('Failed to validate email:', error);
      setEmailValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
    }
  };

  const debounceEmailValidation = React.useCallback(
    debounce((email, excludeUserId) => validateEmail(email, excludeUserId), 500),
    []
  );

  const validateUsername = async (username, excludeUserId = null) => {
    if (!username || !username.trim()) {
      setUsernameValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
      return;
    }

    setUsernameValidation({
      isChecking: true,
      isValid: true,
      message: ''
    });

    try {
      const params = { username: username.trim() };
      if (excludeUserId) {
        params.excludeUserId = excludeUserId;
      }

      const response = await axios.get('/api/users/check-username', { params });

      if (response.data.exists) {
        setUsernameValidation({
          isChecking: false,
          isValid: false,
          message: t('This username is already registered')
        });
      } else {
        setUsernameValidation({
          isChecking: false,
          isValid: true,
          message: ''
        });
      }
    } catch (error) {
      console.error('Failed to validate username:', error);
      setUsernameValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
    }
  };

  const debounceUsernameValidation = React.useCallback(
    debounce((username, excludeUserId) => validateUsername(username, excludeUserId), 500),
    []
  );

  // Check if user is a customer or editor
  const isCustomer = user?.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER');
  const isEditor = user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR');
  const showUsernameField = isCustomer || isEditor;

  useEffect(() => {
    // Update program options based on customer type
    if (profileData.customerType === 'ONLINE') {
      setProgramOptions([
        'Basic 21 Days',
        'Advanced 21 Days',
        '30 Days Weight Loss'
      ]);
    } else if (profileData.customerType === 'CLUB') {
      setProgramOptions([
        'Trial Program',
        'Fitness Plan',
        'Advanced Weight Loss',
        'Premium Weight Loss'
      ]);
    }
  }, [profileData.customerType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailValidation.isValid) {
      setError(t('Please fix the email validation error before submitting.'));
      return;
    }

    if (!usernameValidation.isValid) {
      setError(t('Please fix the username validation error before submitting.'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare data based on user role
      const dataToSend = { ...profileData };
      
      // For SYSTEM_EDITOR users, don't send customer-specific fields
      if (isEditor && !isCustomer) {
        delete dataToSend.customerType;
        delete dataToSend.checkupDay;
        delete dataToSend.program;
      }

      await axios.put('/api/auth/profile', dataToSend);

      setSuccess(true);
      setTimeout(() => {
        window.location.reload(); // Refresh to update user data
      }, 1500);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setError(error.message || t('Failed to update profile. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
              {t('Profile Updated Successfully!')}
            </h3>
            <p style={{ color: 'var(--gray-dark)' }}>
              {t('Your profile information has been updated.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content profile-modal">
        <div className="modal-header">
          <h3>{t('My Profile')}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {error && (
          <div className="alert alert-error">{error}</div>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '24px', marginBottom: '15px' }}>⏳</div>
            <p style={{ color: 'var(--gray-dark)' }}>{t('Loading profile data...')}</p>
          </div>
        )}

        {!loading && profileData.firstName && (
          <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">
                {t('First Name')} *
              </label>
              <input
                type="text"
                id="firstName"
                className="form-input"
                maxLength="50"
                value={profileData.firstName}
                onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName" className="form-label">
                {t('Last Name')} *
              </label>
              <input
                type="text"
                id="lastName"
                className="form-input"
                maxLength="50"
                value={profileData.lastName}
                onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('Email Address')} *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="email"
                id="email"
                className={`form-input ${!emailValidation.isValid ? 'error' : ''}`}
                value={profileData.email}
                onChange={(e) => {
                  const email = e.target.value;
                  setProfileData({...profileData, email: email});
                  debounceEmailValidation(email, user?.id);
                }}
                required
                disabled={loading}
                style={{
                  paddingRight: emailValidation.isChecking ? '40px' : '10px',
                  borderColor: !emailValidation.isValid ? 'var(--error)' : undefined
                }}
              />
              {emailValidation.isChecking && (
                <div style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--primary-blue)'
                }}>
                  ⏳
                </div>
              )}
            </div>
            {!emailValidation.isValid && emailValidation.message && (
              <div style={{
                color: 'var(--error)',
                fontSize: '12px',
                marginTop: '5px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                ❌ {emailValidation.message}
              </div>
            )}
          </div>

          {showUsernameField && (
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                {t('Username')} *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  id="username"
                  className={`form-input ${!usernameValidation.isValid ? 'error' : ''}`}
                  value={profileData.username}
                  onChange={(e) => {
                    const username = e.target.value;
                    setProfileData({...profileData, username});
                    debounceUsernameValidation(username, user?.id);
                  }}
                  required
                  disabled={loading}
                  placeholder={t('Enter username')}
                  style={{
                    paddingRight: usernameValidation.isChecking ? '40px' : '10px',
                    borderColor: !usernameValidation.isValid ? 'var(--error)' : undefined
                  }}
                />
                {usernameValidation.isChecking && (
                  <div style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--primary-blue)'
                  }}>
                    ⏳
                  </div>
                )}
              </div>
              {!usernameValidation.isValid && usernameValidation.message && (
                <div style={{
                  color: 'var(--error)',
                  fontSize: '12px',
                  marginTop: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  ❌ {usernameValidation.message}
                </div>
              )}
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="gender" className="form-label">
                {t('Gender')} *
              </label>
              <select
                id="gender"
                className="form-input"
                value={profileData.gender}
                onChange={(e) => setProfileData({...profileData, gender: e.target.value})}
                required
                disabled={loading}
              >
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                {t('Phone Number')} *
              </label>
              <CountryPhoneInput
                phoneValue={profileData.phone}
                countryValue={profileData.phoneCountryId}
                onPhoneChange={(phone) => setProfileData({...profileData, phone})}
                onCountryChange={(countryId) => setProfileData({...profileData, phoneCountryId: countryId})}
                disabled={loading}
                required={true}
                placeholder={t('Enter phone number')}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address" className="form-label">
              {t('Address')}
            </label>
            <textarea
              id="address"
              className="form-input"
              rows="3"
              value={profileData.address}
              onChange={(e) => setProfileData({...profileData, address: e.target.value})}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="pcId" className="form-label">
              {t('PC ID/Associate ID')}
            </label>
            <input
              type="text"
              id="pcId"
              className="form-input"
              value={profileData.pcId}
              onChange={(e) => setProfileData({...profileData, pcId: e.target.value})}
              disabled={loading}
            />
          </div>

          {isCustomer && (
            <div className="form-group">
              <label htmlFor="checkupDay" className="form-label">
                {t('Checkup Day')} *
              </label>
              <select
                id="checkupDay"
                className="form-input"
                value={profileData.checkupDay}
                onChange={(e) => setProfileData({...profileData, checkupDay: e.target.value})}
                required
                disabled={loading}
              >
                <option value="MONDAY">Monday</option>
                <option value="TUESDAY">Tuesday</option>
                <option value="WEDNESDAY">Wednesday</option>
                <option value="THURSDAY">Thursday</option>
                <option value="FRIDAY">Friday</option>
                <option value="SATURDAY">Saturday</option>
                <option value="SUNDAY">Sunday</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t('New Password (leave blank to keep current)')}
            </label>
            <input
              type="password"
              id="password"
              className="form-input"
              value={profileData.password}
              onChange={(e) => setProfileData({...profileData, password: e.target.value})}
              disabled={loading}
            />
          </div>


          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !emailValidation.isValid || !usernameValidation.isValid}
            >
              {loading ? t('Updating...') : t('Update Profile')}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default ProfileModal;
